(function () {
  const STORAGE_KEY = 'further:wishlist:v1';
  const ACCOUNT_ENDPOINT = '/apps/wantlist/items';
  let accountItems = null;
  let accountAvailable = false;
  let accountChecked = false;
  let accountLoadPromise = null;
  let accountError = '';
  let accountWriteRevision = 0;
  let feedback = null;

  function boot() {
    document.addEventListener('click', handleClick);
    document.addEventListener('product-recommendations:updated', function (event) {
      const root = event.detail && event.detail.root;
      const target = root && typeof root.querySelectorAll === 'function' ? root : document;
      addRecommendationButtons(target);
      syncButtons(target);
    });
    syncButtons(document);
    dispatchChange();
    loadAccountItems().then(function () {
      syncButtons(document);
      dispatchChange();
    }).catch(function () {
      syncButtons(document);
    });
  }

  async function handleClick(event) {
    const toggle = event.target.closest('[data-further-wishlist-toggle]');
    const remove = event.target.closest('[data-further-wishlist-remove]');

    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      const item = itemFromButton(toggle);
      if (!item) return;
      setButtonBusy(toggle, true);
      try {
        await toggleItem(item);
      } catch (error) {
        // The operation displays its error and keeps an idempotent retry action.
      } finally {
        setButtonBusy(toggle, false);
        syncButtons(document);
        dispatchChange();
      }
    } else if (remove) {
      event.preventDefault();
      event.stopPropagation();
      const key = remove.getAttribute('data-further-wishlist-remove');
      if (!key) return;
      setButtonBusy(remove, true);
      try {
        await removeItem(key);
      } catch (error) {
        // Keep the saved heart until removal has been confirmed.
      } finally {
        setButtonBusy(remove, false);
        syncButtons(document);
        dispatchChange();
      }
    }
  }

  function itemFromButton(button) {
    const payload = button.getAttribute('data-wishlist-payload');
    if (payload) {
      try {
        return normalizeItem(JSON.parse(payload));
      } catch (error) {
        return null;
      }
    }

    return normalizeItem({
      product_id: button.getAttribute('data-wishlist-product-id'),
      variant_id: button.getAttribute('data-wishlist-variant-id'),
      sku: button.getAttribute('data-wishlist-sku'),
      title: button.getAttribute('data-wishlist-title'),
      vendor: button.getAttribute('data-wishlist-vendor'),
      handle: button.getAttribute('data-wishlist-handle'),
      url: button.getAttribute('data-wishlist-url'),
      image: button.getAttribute('data-wishlist-image'),
      price: button.getAttribute('data-wishlist-price'),
      compare_at_price: button.getAttribute('data-wishlist-compare-at-price'),
      available: button.getAttribute('data-wishlist-available') !== 'false',
      source: button.getAttribute('data-wishlist-source') || 'native',
    });
  }

  async function toggleItem(item) {
    const key = itemKey(item);
    if (!key) return;
    try {
      await ensureAccountChecked();
    } catch (error) {
      showFeedback('Unable to load your Wantlist. Try again.', function () { return toggleItem(item); });
      throw error;
    }
    if (hasItem(key)) await removeItem(key);
    else await saveItem(item);
  }

  async function saveItem(item, accountRequired = accountAvailable) {
    const normalized = normalizeItem(item);
    const key = itemKey(normalized);
    if (!key) return;

    try {
      await ensureAccountChecked();
      if (accountRequired && !accountAvailable) throw new Error('Sign in again to save this record to your account.');
      accountRequired = accountAvailable;
      if (accountAvailable) await saveAccountItem(normalized);
      else saveLocalItem(normalized);
      clearFeedback();
      syncButtons(document);
      dispatchChange();
    } catch (error) {
      showFeedback(accountRequired && !accountAvailable ? error.message : 'We could not confirm this record was saved. Try saving it again.', function () { return saveItem(normalized, accountRequired); });
      throw error;
    }
  }

  function saveLocalItem(item) {
    const normalized = normalizeItem(item);
    const key = itemKey(normalized);
    if (!key) return;

    const items = getItems().filter(function (existing) {
      return itemKey(existing) !== key;
    });
    items.unshift({
      ...normalized,
      wishlist_key: key,
      saved_at: new Date().toISOString(),
    });
    writeItems(items);
  }

  async function removeItem(key, accountRequired = accountAvailable) {
    try {
      await ensureAccountChecked();
      if (accountRequired && !accountAvailable) throw new Error('Sign in again to remove this record from your account.');
      accountRequired = accountAvailable;
      if (accountAvailable) await removeAccountItem(key);
      else removeLocalItem(key);
      clearFeedback();
      syncButtons(document);
      dispatchChange();
    } catch (error) {
      showFeedback(accountRequired && !accountAvailable ? error.message : 'We could not confirm this record was removed. Try removing it again.', function () { return removeItem(key, accountRequired); });
      throw error;
    }
  }

  function removeLocalItem(key) {
    writeItems(getItems().filter(function (item) {
      return itemKey(item) !== key;
    }));
  }

  function updateLocalItem(key, updates) {
    if (!key || !updates || typeof updates !== 'object') return false;
    let changed = false;
    const items = getLocalItems().map(function (item) {
      if (itemKey(item) !== key) return item;
      changed = true;
      return normalizeItem({
        ...item,
        ...updates,
        product: {
          ...(item.product || {}),
          ...(updates.product || {}),
        },
      }) || item;
    });

    if (changed) {
      writeItems(items);
      dispatchChange();
    }
    return changed;
  }

  function hasItem(itemOrKey) {
    const key = typeof itemOrKey === 'string' ? itemOrKey : itemKey(itemOrKey);
    if (!key) return false;
    return getItems().some(function (item) {
      return itemKey(item) === key;
    });
  }

  function getItems() {
    if (accountAvailable && Array.isArray(accountItems)) {
      return accountItems;
    }
    return accountChecked ? getLocalItems() : [];
  }

  function getLocalItems() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.map(normalizeItem).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  function writeItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, 500)));
  }

  function normalizeItem(item) {
    if (!item || typeof item !== 'object') return null;
    const product = item.product && typeof item.product === 'object' ? item.product : {};
    const audioTracks = arrayValues(item.audio_tracks);
    const productAudioTracks = arrayValues(product.audio_tracks);
    const formatGroups = arrayValues(item.format_groups);
    const productFormatGroups = arrayValues(product.format_groups);
    const productFormats = arrayValues(product.formats);
    const labels = arrayValues(item.labels);
    const genres = arrayValues(item.genre || item.genres);
    const productGenres = arrayValues(product.genre || product.genres);
    const styles = arrayValues(item.style || item.styles);
    const productStyles = arrayValues(product.style || product.styles);
    const availableValue = firstDefined(
      item.available,
      item.available_for_sale,
      product.available,
      product.available_for_sale,
      true
    );

    const normalized = {
      product_id: clean(item.product_id || item.product_gid || product.product_id || product.product_gid),
      variant_id: clean(item.variant_id || item.variant_gid || product.variant_id || product.variant_gid),
      product_gid: clean(item.product_gid || product.product_gid),
      variant_gid: clean(item.variant_gid || product.variant_gid),
      sku: clean(item.sku || product.sku),
      title: clean(product.title || item.title) || 'Untitled product',
      vendor: clean(product.vendor || item.vendor),
      handle: clean(product.handle || item.handle),
      url: clean(product.url || item.url),
      image: clean(product.image || product.image_url || item.image || item.image_url),
      image_alt: clean(product.image_alt || item.image_alt),
      price: moneyNumber(firstDefined(item.price, product.price)),
      compare_at_price: moneyNumber(firstDefined(item.compare_at_price, product.compare_at_price)),
      available: availableValue !== false && availableValue !== 'false',
      cart_url: clean(item.cart_url || product.cart_url),
      variant_title: clean(item.variant_title || product.variant_title),
      source: clean(item.source) || 'native',
      audio_tracks: audioTracks.length ? audioTracks : productAudioTracks,
      format_groups: formatGroups.length ? formatGroups : (productFormatGroups.length ? productFormatGroups : productFormats),
      labels: labels,
      genre: genres.length ? genres : productGenres,
      style: styles.length ? styles : productStyles,
      release_year: item.release_year || product.release_year || null,
      meta: item.meta || product.meta || undefined,
      saved_at: item.saved_at || null,
    };

    if (!itemKey(normalized)) return null;
    if (!normalized.url && normalized.handle) normalized.url = '/products/' + encodeURIComponent(normalized.handle);
    return normalized;
  }

  function itemKey(item) {
    if (!item) return '';
    return shopifyNumericId(item.variant_id || item.variant_gid)
      || shopifyNumericId(item.product_id || item.product_gid)
      || clean(item.sku);
  }

  function syncButtons(root) {
    root.querySelectorAll('[data-further-wishlist-toggle]').forEach(function (button) {
      const item = itemFromButton(button);
      const active = item && hasItem(item);
      const addLabel = button.getAttribute('data-wishlist-add-label') || 'Save';
      const removeLabel = button.getAttribute('data-wishlist-remove-label') || 'Saved';
      const label = active ? removeLabel : addLabel;
      const busy = button.getAttribute('aria-busy') === 'true';
      const textClass = button.classList.contains('further-wishlist-button--icon')
        ? 'visually-hidden'
        : 'further-wishlist-button__text';
      button.classList.toggle('is-saved', Boolean(active));
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', label);
      button.disabled = busy;
      button.innerHTML = [
        '<span class="further-wishlist-button__icon" aria-hidden="true">',
        active ? '&#9829;' : '&#9825;',
        '</span>',
        '<span class="' + textClass + '">',
        escapeHtml(label),
        '</span>',
      ].join('');
    });
  }

  // Shopify can serve a previously rendered recommendations fragment after a theme edit.
  // Those cards still contain the product/variant inputs needed to add the same heart.
  function addRecommendationButtons(root) {
    root.querySelectorAll('.grid__item').forEach(function (card) {
      if (card.querySelector('[data-further-wishlist-toggle]')) return;

      const form = card.querySelector('form[data-type="add-to-cart-form"]');
      const productId = form && form.querySelector('input[name="product-id"]');
      const variantId = form && form.querySelector('input[name="id"]');
      const link = card.querySelector('.card__heading a[href*="/products/"]');
      if (!productId || !variantId || !link) return;

      const productUrl = new URL(link.href, window.location.origin);
      const image = card.querySelector('.card__media img');
      const vendor = card.querySelector('.caption-with-letter-spacing');
      const price = card.querySelector('.price-item--regular, .price-item--sale');
      const submit = form.querySelector('[type="submit"]');
      const button = document.createElement('button');

      card.classList.add('related-products__item');
      button.type = 'button';
      button.className = 'further-wishlist-button further-wishlist-button--icon further-wishlist-button--related';
      button.setAttribute('data-further-wishlist-toggle', '');
      button.setAttribute('data-wishlist-product-id', productId.value);
      button.setAttribute('data-wishlist-variant-id', variantId.value);
      button.setAttribute('data-wishlist-title', link.textContent.trim());
      button.setAttribute('data-wishlist-vendor', vendor ? vendor.textContent.trim() : '');
      button.setAttribute('data-wishlist-handle', productUrl.pathname.split('/').filter(Boolean).pop() || '');
      button.setAttribute('data-wishlist-url', productUrl.pathname);
      button.setAttribute('data-wishlist-image', image ? image.currentSrc || image.src : '');
      button.setAttribute('data-wishlist-price', price ? price.textContent.replace(/[^0-9.]/g, '') : '');
      button.setAttribute('data-wishlist-available', submit && submit.disabled ? 'false' : 'true');
      button.setAttribute('data-wishlist-source', 'native');
      button.setAttribute('data-wishlist-add-label', 'Save');
      button.setAttribute('data-wishlist-remove-label', 'Saved');
      button.setAttribute('aria-pressed', 'false');
      button.innerHTML = '<span class="further-wishlist-button__icon" aria-hidden="true">&#9825;</span><span class="visually-hidden">Save</span>';
      card.appendChild(button);
    });
  }

  function dispatchChange() {
    window.dispatchEvent(new CustomEvent('further:wishlist:change', {
      detail: { items: getItems(), account_backed: accountAvailable, error: accountError },
    }));
  }

  async function ensureAccountChecked() {
    if (accountChecked && !accountError) return;
    await loadAccountItems();
  }

  async function loadAccountItems() {
    if (accountLoadPromise) return accountLoadPromise;
    const revision = accountWriteRevision;

    accountLoadPromise = fetch(ACCOUNT_ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(async function (response) {
        const data = await response.json();
        if (isAnonymousResponse(response, data)) {
          useGuestItems();
          clearFeedback();
          return null;
        }
        if (!response.ok || !data.ok || !data.customer_gid || !Array.isArray(data.items)) {
          throw new Error(data.error || 'Unable to load account wantlist.');
        }
        accountChecked = true;
        accountAvailable = true;
        if (revision === accountWriteRevision) accountItems = data.items.map(normalizeItem).filter(Boolean);
        clearFeedback();
        return accountItems;
      })
      .catch(function (error) {
        accountError = accountAvailable
          ? 'Unable to refresh your Wantlist. Your last confirmed records are still shown.'
          : 'Unable to load your Wantlist. Try again.';
        showFeedback(accountError, loadAccountItems);
        throw error;
      })
      .finally(function () {
        accountLoadPromise = null;
        syncButtons(document);
        dispatchChange();
      });

    return accountLoadPromise;
  }

  async function saveAccountItem(item) {
    try {
      const response = await fetch(ACCOUNT_ENDPOINT, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(accountItemPayload(item)),
      });
      const data = await response.json();
      if (isAnonymousResponse(response, data)) throw new Error('Your account session ended. Refresh your Wantlist before trying again.');
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Unable to save account wantlist item.');
      }
      const savedItem = normalizeItem(Object.assign({}, item, data.item || {}));
      const key = itemKey(savedItem);
      accountItems = (accountItems || []).filter(function (existing) {
        return itemKey(existing) !== key;
      });
      accountItems.unshift({
        ...savedItem,
        wishlist_key: key,
        saved_at: new Date().toISOString(),
      });
      accountAvailable = true;
      accountWriteRevision += 1;
      return true;
    } catch (error) {
      accountError = error.message || 'Unable to save account wantlist item.';
      throw error;
    }
  }

  async function removeAccountItem(keyOrItem) {
    const key = typeof keyOrItem === 'string' ? keyOrItem : itemKey(keyOrItem);
    if (!key) return false;

    try {
      const response = await fetch(ACCOUNT_ENDPOINT, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ variant_id: key }),
      });
      const data = await response.json();
      if (isAnonymousResponse(response, data)) throw new Error('Your account session ended. Refresh your Wantlist before trying again.');
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Unable to remove account wantlist item.');
      }
      accountItems = (accountItems || []).filter(function (item) {
        return itemKey(item) !== key;
      });
      accountAvailable = true;
      accountWriteRevision += 1;
      return true;
    } catch (error) {
      accountError = error.message || 'Unable to remove account wantlist item.';
      throw error;
    }
  }

  function isAnonymousResponse(response, data) {
    return response.status === 401 && data && data.code === 'anonymous_customer';
  }

  function useGuestItems() {
    accountChecked = true;
    accountAvailable = false;
    accountItems = null;
  }

  function clearFeedback() {
    accountError = '';
    if (feedback) feedback.hidden = true;
  }

  function showFeedback(message, retry) {
    if (!document.body) return;
    if (!feedback) {
      feedback = document.createElement('div');
      feedback.className = 'further-wishlist-feedback';
      feedback.setAttribute('role', 'alert');
      document.body.appendChild(feedback);
    }
    feedback.textContent = '';
    const text = document.createElement('span');
    text.textContent = message;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button--secondary';
    button.textContent = 'Try again';
    button.addEventListener('click', async function () {
      button.disabled = true;
      try {
        await retry();
      } catch (error) {
        // The failed operation replaces this feedback with its retry action.
      } finally {
        button.disabled = false;
      }
    });
    feedback.appendChild(text);
    feedback.appendChild(button);
    feedback.hidden = false;
  }

  function accountItemPayload(item) {
    return {
      product_id: item.product_gid || item.product_id,
      variant_id: item.variant_gid || item.variant_id,
      sku: item.sku,
      source: item.source || 'native',
    };
  }

  function shopifyNumericId(value) {
    const cleaned = clean(value);
    const match = cleaned.match(/\/(\d+)$/);
    return match ? match[1] : cleaned;
  }

  function setButtonBusy(button, busy) {
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function clean(value) {
    return (value === null || value === undefined) ? '' : String(value).trim();
  }

  function moneyNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(String(value).replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  function firstDefined() {
    for (let index = 0; index < arguments.length; index += 1) {
      if (arguments[index] !== null && arguments[index] !== undefined && arguments[index] !== '') {
        return arguments[index];
      }
    }
    return null;
  }

  function arrayValues(value) {
    if (Array.isArray(value)) return value.map(clean).filter(Boolean);
    if (value === null || value === undefined || value === '') return [];
    return [clean(value)].filter(Boolean);
  }

  function escapeHtml(value) {
    return clean(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  window.FurtherWishlist = {
    getItems,
    hasItem,
    saveItem,
    removeItem,
    syncButtons,
    refresh: loadAccountItems,
    updateLocalItem,
    isAccountBacked: function () { return accountAvailable; },
    isAccountChecked: function () { return accountChecked; },
    getError: function () { return accountError; },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
